import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { hasValidFirebaseEnv } from '@/lib/firebase'
import { 
  Activity, 
  Shield, 
  Smartphone, 
  Watch,
  ArrowRight,
  AlertCircle,
  Loader2
} from 'lucide-react'
import { cn } from '@/lib/utils'

export function LoginPage() {
  const { error, signInWithGoogle, signInWithEmail, loading } = useAuth()
  const [isHovered, setIsHovered] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const features = [
    { icon: Activity, label: 'Health Analytics', desc: 'Real-time metrics' },
    { icon: Shield, label: 'Secure Access', desc: 'Role-based controls' },
    { icon: Smartphone, label: 'User Management', desc: 'Monitor & support' },
    { icon: Watch, label: 'Device Control', desc: 'Watchface & settings' },
  ]

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Left Side - Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-rose-600/10 to-purple-900/20" />
        
        {/* Animated Circles */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-rose-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center overflow-hidden outline outline-1 outline-white/10 outline-offset-[-1px]">
              <img src="/adaptive_foreground_icon.jpeg" alt="Logo" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">Parlent</span>
          </div>

          {/* Hero Text */}
          <div className="max-w-md">
            <h1 className="text-4xl font-bold text-white leading-tight mb-4">
              Admin Control
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-rose-400">
                Center
              </span>
            </h1>
            <p className="text-stone-400 text-lg leading-relaxed">
              Comprehensive dashboard for managing users, content, products, and health analytics.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="group p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
              >
                <feature.icon className="w-6 h-6 text-orange-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-white font-medium text-sm">{feature.label}</h3>
                <p className="text-stone-500 text-xs mt-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center overflow-hidden outline outline-1 outline-white/10 outline-offset-[-1px]">
              <img src="/adaptive_foreground_icon.jpeg" alt="Logo" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold text-white">Parlent</span>
          </div>

          {/* Login Card */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-600 to-rose-600 rounded-3xl blur opacity-20" />
            
            <div className="relative bg-[#141414] rounded-3xl border border-stone-800 p-8 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Welcome Back</h2>
                <p className="text-stone-500 text-sm">
                  Sign in with your admin Google account
                </p>
              </div>

              {/* Error Alert */}
              {error && (
                <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-rose-400 text-sm font-medium">Access Denied</p>
                    <p className="text-rose-400/70 text-xs mt-1">{error}</p>
                  </div>
                </div>
              )}

              {!hasValidFirebaseEnv && (
                <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <p className="text-amber-400 text-sm">
                    Firebase configuration is missing. Please check your environment variables.
                  </p>
                </div>
              )}

              {/* Google Sign In Button */}
              <button
                onClick={() => void signInWithGoogle()}
                disabled={loading || !hasValidFirebaseEnv}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={cn(
                  "w-full group relative flex items-center justify-center gap-3",
                  "px-6 py-4 rounded-2xl font-medium text-white",
                  "transition-all duration-300",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  isHovered 
                    ? "bg-gradient-to-r from-orange-500 to-rose-500 shadow-lg shadow-orange-500/25" 
                    : "bg-gradient-to-r from-orange-600 to-rose-600"
                )}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    {/* Google Icon */}
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Continue with Google</span>
                    <ArrowRight className={cn(
                      "w-5 h-5 transition-transform duration-300",
                      isHovered ? "translate-x-1" : ""
                    )} />
                  </>
                )}
              </button>

              {/* Pemisah */}
              <div className="flex items-center gap-4 my-5">
                <div className="flex-1 h-px bg-stone-800" />
                <span className="text-stone-600 text-xs uppercase tracking-wider">atau</span>
                <div className="flex-1 h-px bg-stone-800" />
              </div>

              {/* Email & password: jalur cadangan bila browser memblokir Google */}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void signInWithEmail(email, password)
                }}
                className="space-y-3"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email admin"
                  autoComplete="username"
                  required
                  className="w-full rounded-2xl border border-stone-800 bg-stone-900/60 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 outline-none transition focus:border-orange-500/60"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  className="w-full rounded-2xl border border-stone-800 bg-stone-900/60 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 outline-none transition focus:border-orange-500/60"
                />
                <button
                  type="submit"
                  disabled={loading || !hasValidFirebaseEnv || !email || !password}
                  className="w-full rounded-2xl border border-stone-700 bg-stone-900 px-6 py-3 text-sm font-medium text-stone-200 transition hover:border-orange-500/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Signing in...' : 'Masuk dengan Email'}
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-stone-800" />
                <span className="text-stone-600 text-xs uppercase tracking-wider">Admin Only</span>
                <div className="flex-1 h-px bg-stone-800" />
              </div>

              {/* Info */}
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-stone-900/50 border border-stone-800">
                  <Shield className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-stone-300 text-sm font-medium">Secure Access</p>
                    <p className="text-stone-500 text-xs mt-0.5">
                      Only authorized administrators can access this dashboard.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-xl bg-stone-900/50 border border-stone-800">
                  <Activity className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-stone-300 text-sm font-medium">Role-Based Permissions</p>
                    <p className="text-stone-500 text-xs mt-0.5">
                      Your access level is determined by your assigned role.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-stone-600 text-xs mt-8">
            © 2026 Parlent. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
